import { useEffect, useRef } from 'react';
import { Swiper as SwiperType } from 'swiper/types';

interface UseSwiperAutoplayOnVisibleOptions {
  open?: boolean;
  /**
   * IntersectionObserver 的阈值（0-1）
   * @default 0.1
   */
  threshold?: number;
  /**
   * 是否在初始化时立即检查可见性
   * @default true
   */
  initialCheck?: boolean;
  /**
   * 重制动画
   */
  resetProgress?: () => void;
  [key: string]: any;
}

const DEFAULT_OPTIONS: UseSwiperAutoplayOnVisibleOptions = {
  open: false,
  threshold: 0.1,
  initialCheck: true,
}

export const useSwiperAutoplayOnVisible = (
  swiperRef: React.MutableRefObject<SwiperType | undefined>,
  options: UseSwiperAutoplayOnVisibleOptions = DEFAULT_OPTIONS,
  swiperAutoPlay: boolean = false
) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isInitialMount = useRef(true);
  const curOptions = {...DEFAULT_OPTIONS, ...options}

  useEffect(() => {
    const swiper = swiperRef?.current;
    if (!swiper || !curOptions.open || !swiperAutoPlay) return;

    // 视口检测逻辑
    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // 进入视口且未运行时才启动
          if (swiper.autoplay?.running === false) {
            swiper.autoplay.start();
            curOptions.resetProgress?.();
          }
        } else {
          // 离开视口时停止
          if (swiper.autoplay?.running) {
            swiper.autoplay.stop();
          }
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, {
      threshold: curOptions?.threshold ?? 0.1,
    });

    observer.observe(swiper.el);
    observerRef.current = observer;

    // 初始可见性检查
    if (curOptions?.initialCheck !== false && isInitialMount.current) {
      const rect = swiper.el.getBoundingClientRect();
      const isVisible =
        rect.top <= window.innerHeight &&
        rect.bottom >= 0 &&
        rect.left <= window.innerWidth &&
        rect.right >= 0;

      if (isVisible && swiper.autoplay?.running === false) {
        swiper.autoplay.start();
        curOptions.resetProgress?.();
      }
      isInitialMount.current = false;
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [
    swiperRef,
    curOptions?.threshold,
    curOptions?.initialCheck,
  ]);
};